import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  fetchReport,
  getAdminCustomerSoa,
  listAdminConsignmentCustomers,
  type ConsignmentCustomerRow,
} from '../../lib/api';
import { CONSIGNMENT_PERMS } from '@mlm/shared';
import { downloadReport, type ReportExportMeta } from '../../lib/report-export';
import { ReportExportButtons } from '../../components/report-export-buttons';
import { useHasPerm } from '../../lib/auth-store';
import { Money } from './_shared';

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

/**
 * Peso display for the decimal STRINGS the accounting routes return.
 * Display-only formatting — no money math happens here.
 */
function peso(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
}
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Admin read-only consignment view: customers with stock out on consignment,
 * the outstanding-balances report, and a printable per-customer Statement of
 * Account. All documents are created at the till (cashier app) — this page
 * only reads.
 */
export function ConsignmentAdminPage() {
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [selected, setSelected] = useState<ConsignmentCustomerRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const customersQ = useQuery({
    queryKey: ['consignment-customers', qDebounced],
    queryFn: () => listAdminConsignmentCustomers(qDebounced || undefined),
  });

  const customers = customersQ.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Consignment</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Stock out on consignment, per consignee, with a printable Statement of Account.
        </p>
      </div>

      <p className="rounded-md border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
        Consignment documents are created at the till (cashier app). Billings here are outside the
        Sales reports.
      </p>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>
            Consignees{' '}
            <span className="text-sm font-normal text-muted-foreground">({customers.length})</span>
          </CardTitle>
          <Input
            className="h-9 w-64"
            placeholder="Search name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </CardHeader>
        <CardContent className="p-0">
          {customersQ.isLoading ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">Loading…</p>
          ) : customersQ.isError ? (
            <p className="px-4 pb-4 text-sm text-destructive">
              {errText(customersQ.error) ?? 'Failed to load consignees.'}
            </p>
          ) : customers.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">
              {qDebounced ? 'No consignees match the search.' : 'No consignees yet.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Consignee</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2 text-right">Units out</th>
                    <th className="px-4 py-2 text-right">Open billings</th>
                    <th className="px-4 py-2 text-right">Receivable</th>
                    <th className="px-4 py-2">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr
                      key={c.customer_user_id}
                      className={
                        'cursor-pointer border-b last:border-0 hover:bg-muted/30 ' +
                        (selected?.customer_user_id === c.customer_user_id ? 'bg-muted/40' : '')
                      }
                      onClick={() => setSelected(c)}
                    >
                      <td className="px-4 py-2 font-medium">{c.customer_name}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{c.email}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {c.units.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {c.open_billings.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Money>{peso(c.receivable)}</Money>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {shortDate(c.last_activity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!customersQ.isLoading && customers.length > 0 && (
            <p className="border-t px-4 py-2 text-xs text-muted-foreground">
              Click a consignee to view their Statement of Account.
            </p>
          )}
        </CardContent>
      </Card>

      {selected && (
        <SoaPanel
          key={selected.customer_user_id}
          customer={selected}
          onClose={() => setSelected(null)}
        />
      )}

      <OutstandingReportCard />
    </div>
  );
}

/**
 * The consignment-outstanding report, verbatim from the reports registry, now
 * exportable as CSV / Excel / PDF.
 *
 * GRANDFATHERED: all three formats ride the single advisory
 * `consignment.export` code rather than the global `export.*` codes, so a
 * tenant that already unchecked "Export" for consignment is not re-opened by
 * the per-format rollout — and nothing here is double-gated.
 */
const CONSIGNMENT_EXPORT_PERMS = {
  csv: CONSIGNMENT_PERMS.export,
  excel: CONSIGNMENT_PERMS.export,
  pdf: CONSIGNMENT_PERMS.export,
};

const OUTSTANDING_META: ReportExportMeta = {
  title: 'Outstanding Consignments',
  sheetName: 'Outstanding',
  subtitle: ['Unsettled consignee balances as of the generation time'],
};

function OutstandingReportCard() {
  const reportQ = useQuery({
    queryKey: ['report', 'consignment-outstanding'],
    queryFn: () => fetchReport('consignment-outstanding', {}),
  });
  const data = reportQ.data;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Outstanding consignments</CardTitle>
        <ReportExportButtons
          result={data}
          meta={OUTSTANDING_META}
          perms={CONSIGNMENT_EXPORT_PERMS}
          onCsv={() =>
            downloadReport('consignment-outstanding', {}, 'csv', {
              ...OUTSTANDING_META,
              result: data,
            })
          }
        />
      </CardHeader>
      <CardContent className="p-0">
        {reportQ.isLoading ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">Loading…</p>
        ) : reportQ.isError ? (
          <p className="px-4 pb-4 text-sm text-destructive">
            {errText(reportQ.error) ?? 'Failed to load the report.'}
          </p>
        ) : !data || data.rows.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">Nothing outstanding.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  {data.columns.map((c) => (
                    <th
                      key={c.key}
                      className={
                        'px-4 py-2 font-medium ' + (c.align === 'right' ? 'text-right' : '')
                      }
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b last:border-0">
                    {data.columns.map((c) => (
                      <td
                        key={c.key}
                        className={
                          'px-4 py-1.5 text-xs ' +
                          (c.align === 'right' ? 'text-right font-mono tabular-nums' : '')
                        }
                      >
                        {row[c.key] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.summary?.length ? (
              <div className="flex flex-wrap gap-x-6 gap-y-1 border-t px-4 py-3 text-xs">
                {data.summary.map((s) => (
                  <span key={s.label}>
                    <span className="text-muted-foreground">{s.label}: </span>
                    <span className="font-medium tabular-nums">{s.value}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Per-customer Statement of Account. The statement body carries the
 * `.packing-slip` class so `window.print()` emits it alone on an A4 sheet
 * (see index.css) — same mechanism as the orders packing slip.
 */
function SoaPanel({
  customer,
  onClose,
}: {
  customer: ConsignmentCustomerRow;
  onClose: () => void;
}) {
  const canPrint = useHasPerm(CONSIGNMENT_PERMS.print);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());

  const soaQ = useQuery({
    queryKey: ['consignment-soa', customer.customer_user_id, from, to],
    // Date-only strings parse as UTC midnight server-side, which in Manila
    // closes the window at 8 AM of the "to" day and drops same-day documents
    // (they carry real timestamps). Send local day bounds instead.
    queryFn: () =>
      getAdminCustomerSoa(customer.customer_user_id, {
        ...(from ? { from: new Date(`${from}T00:00:00`).toISOString() } : {}),
        ...(to ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
      }),
  });
  const soa = soaQ.data;

  const printSoa = () => {
    const prev = document.title;
    document.title = 'Statement of Account';
    window.print();
    document.title = prev;
  };

  const periodLabel = `${from ? shortDate(from) : 'Start'} – ${to ? shortDate(to) : 'Today'}`;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>
          Statement of Account{' '}
          <span className="text-sm font-normal text-muted-foreground">
            · {customer.customer_name}
          </span>
        </CardTitle>
        <div className="flex items-center gap-2">
          {canPrint && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={printSoa}
              disabled={!soa}
            >
              <Printer className="h-4 w-4" /> Print SOA
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              className="h-9 w-40"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input
              type="date"
              className="h-9 w-40"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        {soaQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : soaQ.isError ? (
          <p className="text-sm text-destructive">
            {errText(soaQ.error) ?? 'Failed to load the statement.'}
          </p>
        ) : !soa ? null : (
          <div className="packing-slip rounded-md border bg-background p-6 text-foreground">
            <header className="flex items-start justify-between gap-6 border-b pb-4">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">STATEMENT OF ACCOUNT</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">Period: {periodLabel}</p>
              </div>
              <div className="text-right">
                <p className="text-base font-semibold leading-tight">{soa.customer.name}</p>
                <p className="text-xs text-muted-foreground">{soa.customer.email}</p>
                {soa.customer.phone && (
                  <p className="text-xs text-muted-foreground">{soa.customer.phone}</p>
                )}
              </div>
            </header>

            <p className="flex items-baseline justify-between border-b py-3 text-sm">
              <span className="text-muted-foreground">Opening balance</span>
              <Money>{peso(soa.openingBalance)}</Money>
            </p>

            <table className="w-full text-sm">
              <thead className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-2 pr-2">Date</th>
                  <th className="py-2 pr-2">Doc</th>
                  <th className="py-2 pr-2">No</th>
                  <th className="py-2 pr-2">Ref</th>
                  <th className="py-2 pr-2 text-right">Charges</th>
                  <th className="py-2 pr-2 text-right">Payments</th>
                  <th className="py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {soa.lines.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-xs text-muted-foreground">
                      No activity in this period.
                    </td>
                  </tr>
                ) : (
                  soa.lines.map((l, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">
                        {shortDate(l.date)}
                      </td>
                      <td className="py-1.5 pr-2 text-xs">{l.docType}</td>
                      <td className="py-1.5 pr-2 font-mono text-xs">{l.docNo}</td>
                      <td className="py-1.5 pr-2 font-mono text-xs text-muted-foreground">
                        {l.ref ?? '—'}
                      </td>
                      <td className="py-1.5 pr-2 text-right">
                        <Money>{peso(l.charges)}</Money>
                      </td>
                      <td className="py-1.5 pr-2 text-right">
                        <Money>{peso(l.payments)}</Money>
                      </td>
                      <td className="py-1.5 text-right">
                        <Money>{peso(l.balance)}</Money>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td className="py-2 pr-2" colSpan={4}>
                    Totals
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <Money>{peso(soa.totalCharges)}</Money>
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <Money>{peso(soa.totalPayments)}</Money>
                  </td>
                  <td className="py-2 text-right">
                    <Money>{peso(soa.closingBalance)}</Money>
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="mt-4 flex items-baseline justify-between border-t pt-3">
              <span className="text-sm font-medium">Closing balance</span>
              <span className="text-base font-semibold">
                <Money>{peso(soa.closingBalance)}</Money>
              </span>
            </div>

            <footer className="mt-6 border-t pt-3 text-center text-xs text-muted-foreground">
              This is not an official receipt.
            </footer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
